// @ts-nocheck
import { getFirestore, doc, getDoc, collection, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { serverTimestamp } from 'firebase/firestore';

const HOSPITALS_COLLECTION = 'hospitals';
const hospitalsDbName = `${import.meta.env.VITE_HOSPITALS_DB_PREFIX}-${import.meta.env.VITE_LOCATION_SHORTHAND}`

function normalizeHospitalName(value) {
  return String(value || '').trim().toLowerCase();
}

function buildHospitalFilterConfig({ nameFilter, nameFilterOperator, emailFilter, emailFilterOperator }) {
  const normalizedNameFilter = normalizeHospitalName(nameFilter);
  const normalizedEmailFilter = emailFilter?.trim().toLowerCase() || '';

  if (normalizedNameFilter) {
    return {
      fieldName: 'nameLowerCase',
      sourceFieldName: 'name',
      filterValue: normalizedNameFilter,
      filterOperator: nameFilterOperator || 'startsWith',
    };
  }

  if (normalizedEmailFilter) {
    return {
      fieldName: 'contactEmail',
      filterValue: normalizedEmailFilter,
      filterOperator: emailFilterOperator || 'startsWith',
    };
  }

  return null;
}

function buildHospitalListConstraints({ pageSize, cursorSnapshot }) {
  return [
    orderBy('createdAt', 'desc'),
    ...(cursorSnapshot ? [startAfter(cursorSnapshot)] : []),
    limit(pageSize),
  ];
}

function matchesHospitalFilter(hospital, filterConfig) {
  if (!filterConfig) {
    return true;
  }

  const sourceValue = String(hospital?.[filterConfig.sourceFieldName || filterConfig.fieldName] || '').trim().toLowerCase();

  if (filterConfig.filterOperator === 'equals') {
    return sourceValue === filterConfig.filterValue;
  }

  return sourceValue.startsWith(filterConfig.filterValue);
}

export async function getHospital(id) {
  if (!id) throw new Error('getHospital: missing id');
  const db = getFirestore(hospitalsDbName);
  const hospRef = doc(db, HOSPITALS_COLLECTION, id);
  const snapshot = await getDoc(hospRef);
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function listHospitals({
  page = 0,
  pageSize = 10,
  cursorSnapshot = null,
  includeTotalCount = false,
  nameFilter = '',
  nameFilterOperator = 'startsWith',
  emailFilter = '',
  emailFilterOperator = 'startsWith',
} = {}) {
  const db = getFirestore(hospitalsDbName);
  const hospitalsRef = collection(db, HOSPITALS_COLLECTION);
  const filterConfig = buildHospitalFilterConfig({
    nameFilter,
    nameFilterOperator,
    emailFilter,
    emailFilterOperator,
  });
  if (filterConfig) {
    const { fieldName, filterValue, filterOperator } = filterConfig;
    const filterConstraints =
      filterOperator === 'equals'
        ? [where(fieldName, '==', filterValue)]
        : [
            where(fieldName, '>=', filterValue),
            where(fieldName, '<=', filterValue + '\uf8ff'),
          ];

    const filteredQuery = query(
      hospitalsRef,
      ...filterConstraints,
      orderBy(fieldName, 'asc'),
      ...(cursorSnapshot ? [startAfter(cursorSnapshot)] : []),
      limit(pageSize + 1),
    );

    const snapshot = await getDocs(filteredQuery);
    const hasMore = snapshot.docs.length > pageSize;
    const pageDocs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;
    const hospitals = pageDocs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
    const lastVisibleSnapshot = pageDocs.length ? pageDocs[pageDocs.length - 1] : null;

    return { hospitals, totalCount: null, lastVisibleSnapshot, hasMore };
  }

  const listConstraints = buildHospitalListConstraints({ pageSize, cursorSnapshot });
  const q = query(
    hospitalsRef,
    orderBy('createdAt', 'desc'),
    ...(cursorSnapshot ? [startAfter(cursorSnapshot)] : []),
    limit(pageSize + 1),
  );
  const snapshot = await getDocs(q);
  const hasMore = snapshot.docs.length > pageSize;
  const pageDocs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;
  const hospitals = pageDocs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
  const lastVisibleSnapshot = pageDocs.length ? pageDocs[pageDocs.length - 1] : null;

  return { hospitals, totalCount: null, lastVisibleSnapshot, hasMore };
}

export async function createHospital(data) {
  if (!data || !data.name) {
    return Promise.reject(new Error('createHospital: name is required'));
  }

  const db = getFirestore(hospitalsDbName);
  const now = serverTimestamp();
  const payload = {
    name: data.name,
    nameLowerCase: normalizeHospitalName(data.name),
    address: data.address || '',
    country: data.country || '',
    contactEmail: data.contactEmail || '',
    createdAt: now,
    createdBy: data.createdBy || null,
    updatedAt: now,
    updatedBy: data.updatedBy || null,
    ...data,
  };

  // ensure known fields override any extras from ...data
  const { name, nameLowerCase, address, country, contactEmail, createdAt, createdBy, updatedAt, updatedBy } = payload;
  const hospDoc = await addDoc(collection(db, HOSPITALS_COLLECTION), { name, nameLowerCase, address, country, contactEmail, createdAt, createdBy, updatedAt, updatedBy });
  return getHospital(hospDoc.id);
}

export async function updateHospital(data) {
  if (!data || !data.id) {
    return Promise.reject(new Error('updateHospital: id is required'));
  }

  const db = getFirestore(hospitalsDbName);
  const hospRef = doc(db, HOSPITALS_COLLECTION, data.id);
  const updates = {
    name: data.name || '',
    nameLowerCase: normalizeHospitalName(data.name),
    address: data.address || '',
    country: data.country || '',
    contactEmail: data.contactEmail || '',
    updatedAt: serverTimestamp(),
    updatedBy: data.updatedBy || null,
  };

  await updateDoc(hospRef, updates);
  return getHospital(data.id);
}

export async function deleteHospital(id) {
  if (!id) return Promise.reject(new Error('deleteHospital: missing id'));
  const db = getFirestore(hospitalsDbName);
  const hospRef = doc(db, HOSPITALS_COLLECTION, id);
  await deleteDoc(hospRef);
  return { id };
}
